import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive, Pagination } from "@/pages/Senders";
import {
  Database, Plus, Trash, LightningSlash, Lightning,
  CheckCircle, Warning, Clock, DownloadSimple,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { useAiModels, ModelOptions } from "@/lib/aiModels";

/**
 * Content Pool — pre-generate AI email content during off-peak hours so
 * live campaigns never wait on DeepSeek. Each pool item carries the
 * verbatim placeholder tokens so it can safely bind to any recipient
 * at send-time (1 item → 1 recipient, consumed and deleted).
 */
export default function ContentPool() {
  const { models: aiModels } = useAiModels();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  // Form is hydrated ONCE on first load and again after each Save. The
  // 5s poll only updates `state` (stats + items + last_run_status). This
  // way, typing "6000" into target_count is never clobbered mid-edit.
  const [form, setForm] = useState({
    target_count: "",
    overage_percent: 25,
    auto_enabled: true,
    pool_concurrency_auto: 3,
    pool_concurrency_manual: 5,
    restrict_to_off_hours: false,
    pool_auto_model: "",
    pool_manual_model: "",
    pool_auto_thinking: "",
    pool_manual_thinking: "",
    ai_batch_size: 1,
  });
  const [formHydrated, setFormHydrated] = useState(false);
  const [manualCount, setManualCount] = useState(100);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [busy, setBusy] = useState(false);
  const [dlCount, setDlCount] = useState("");
  const [dlDelete, setDlDelete] = useState(false);
  const [dlWhich, setDlWhich] = useState("all");
  const [throttled, setThrottled] = useState([]);
  const [cleanupStats, setCleanupStats] = useState(null);
  // Reused-content mix: % of sends that draw a used-once item for its 2nd use
  // (the rest draw fresh). Held locally so dragging never fights the 5s poll.
  const [mixPct, setMixPct] = useState(30);

  const hydrateForm = (cfg) => {
    setForm({
      target_count: cfg?.target_count ?? 0,
      overage_percent: cfg?.overage_percent ?? 25,
      auto_enabled: cfg?.auto_enabled ?? true,
      pool_concurrency_auto: cfg?.pool_concurrency_auto ?? cfg?.pool_concurrency ?? 3,
      pool_concurrency_manual: cfg?.pool_concurrency_manual ?? 5,
      restrict_to_off_hours: cfg?.restrict_to_off_hours ?? false,
      pool_auto_model: cfg?.pool_auto_model ?? "",
      pool_manual_model: cfg?.pool_manual_model ?? "",
      pool_auto_thinking: cfg?.pool_auto_thinking ?? "",
      pool_manual_thinking: cfg?.pool_manual_thinking ?? "",
      ai_batch_size: cfg?.ai_batch_size ?? 1,
    });
    setMixPct(Math.round(((cfg?.recycle_mix_weight ?? 0.3)) * 100));
  };

  const load = async ({ hydrate } = {}) => {
    try {
      const r = await api.get("/content-pool");
      setState(r.data);
      if (hydrate) hydrateForm(r.data.config);
      const it = await api.get("/content-pool/items", { params: { limit: pageSize, skip: page * pageSize } });
      setItems(it.data.items);
      setTotal(it.data.total);
      try {
        const th = await api.get("/ai/throttled");
        setThrottled(th.data.throttled || []);
      } catch (_e) { /* non-critical */ }
      try {
        const cs = await api.get("/content-pool/cleanup-stats");
        setCleanupStats(cs.data);
      } catch (_e) { /* non-critical */ }
    } catch (_e) {
      // silent
    }
  };

  // First load hydrates the form; subsequent 5s polls do NOT touch it.
  useEffect(() => {
    (async () => {
      await load({ hydrate: true });
      setFormHydrated(true);
    })();
    const iv = setInterval(() => load({ hydrate: false }), 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const setSweepMode = async (mode) => {
    try {
      await api.put("/content-pool/sweep-mode", { mode });
      setCleanupStats((cs) => (cs ? { ...cs, sweep_mode: mode } : cs));
      toast.success(mode === "turbo" ? "Repair sweep set to TURBO (fast drain)" : "Repair sweep set to GENTLE (low CPU — safe during sends)");
      load({ hydrate: false });
    } catch (_e) {
      toast.error("Could not change sweep mode");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await api.put("/content-pool/config", {
        target_count: Number(form.target_count) || 0,
        overage_percent: Number(form.overage_percent) || 0,
        auto_enabled: !!form.auto_enabled,
        pool_concurrency_auto: Number(form.pool_concurrency_auto) || 3,
        pool_concurrency_manual: Number(form.pool_concurrency_manual) || 5,
        restrict_to_off_hours: !!form.restrict_to_off_hours,
        pool_auto_model: form.pool_auto_model || "deepseek_direct_flash",
        pool_manual_model: form.pool_manual_model || "deepseek_direct_flash",
        pool_auto_thinking: form.pool_auto_thinking || "",
        pool_manual_thinking: form.pool_manual_thinking || "",
        ai_batch_size: Number(form.ai_batch_size) || 1,
      });
      toast.success("Pool config saved");
      hydrateForm(r.data.config);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const manualFill = async () => {
    if (manualCount <= 0) return;
    setBusy(true);
    try {
      const r = await api.post("/content-pool/generate", {
        count: Number(manualCount),
        model: form.pool_manual_model || "deepseek_direct_flash",
        thinking: form.pool_manual_thinking || "",
      });
      toast.success(`Queued ${r.data.queued} manual generations (concurrency ${state?.concurrency_manual || 5}, model ${form.pool_manual_model || "deepseek_direct_flash"})`);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Queue failed");
    } finally { setBusy(false); }
  };

  const cancelQueue = async () => {
    if (!confirm("Cancel pending manual queue?")) return;
    await api.delete("/content-pool/queue");
    toast.success("Manual queue cleared");
    load({ hydrate: false });
  };

  const clearPool = async () => {
    if (!confirm("Delete every ready pool item? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await api.delete("/content-pool");
      toast.success(`Cleared ${r.data.deleted} pool item${r.data.deleted === 1 ? "" : "s"}`);
      await load({ hydrate: false });
    } finally { setBusy(false); }
  };

  const clearBrokenItems = async () => {
    if (!confirm("Delete pool items that are unusable — empty body/subject, 'Quick note' fallback, CTA anchor in the intro paragraph, OR greeting missing the [firstName] placeholder. Keeps healthy items intact.")) return;
    setBusy(true);
    try {
      const r = await api.delete("/content-pool/broken");
      const brk = r.data.breakdown || {};
      const parts = [];
      if (brk.empty_or_quick_note) parts.push(`${brk.empty_or_quick_note} empty/quick-note`);
      if (brk.cta_in_intro) parts.push(`${brk.cta_in_intro} CTA-in-intro`);
      if (brk.missing_firstname) parts.push(`${brk.missing_firstname} no-[firstName]`);
      const detail = parts.length ? ` (${parts.join(" + ")})` : "";
      toast.success(`Removed ${r.data.deleted} broken pool item${r.data.deleted === 1 ? "" : "s"}${detail}`);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setBusy(false); }
  };

  const clearHardcodedNameItems = async () => {
    if (!confirm("Delete pool items whose greeting uses a HARDCODED first name (e.g. 'Hey James,') instead of the [firstName] placeholder? Keeps clean items intact.")) return;
    setBusy(true);
    try {
      const r = await api.delete("/content-pool/hardcoded-names");
      toast.success(`Removed ${r.data.deleted} item${r.data.deleted === 1 ? "" : "s"} with hardcoded names`);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setBusy(false); }
  };

  const clearRepetitivePhraseItems = async () => {
    if (!confirm("Clean the ready pool. DUPLICATE BODIES (exact or near) are deleted. SUBJECTS are NEVER deleted — if a subject is a DUPLICATE or TOO SHORT (under 40 visible chars, placeholders don't count), the (expensive) body is kept and only a fresh 40–60 char subject is regenerated in its place, in the background. Items are deleted only if the BODY is repetitive, thin/short, or has a broken CTA. Finally, emails are trimmed so any extra recipient-detail placeholder ([phone], [lastName], [address], [city], [state], [zip], [email], [companyName]) stays in under 25% of the pool. A background sweep also runs automatically every 4 minutes.")) return;
    setBusy(true);
    try {
      const r = await api.delete("/content-pool/repetitive-phrases");
      const b = r.data.breakdown || {};
      const parts = [];
      if (b.merged_subject) parts.push(`${b.merged_subject} merged subject`);
      if (b.subject_echoed_in_body) parts.push(`${b.subject_echoed_in_body} subject echoed in body`);
      if (b.contact_over_cap || b.optional_over_cap) parts.push(`${b.optional_over_cap ?? b.contact_over_cap} extra-placeholder over 25%`);
      if (b.repetitive_body) parts.push(`${b.repetitive_body} repetitive body`);
      if (b.cta_defects) parts.push(`${b.cta_defects} bad CTA`);
      if (b.loanamount_or_sender_dupe) parts.push(`${b.loanamount_or_sender_dupe} duplicate [loanAmount]/[sender]`);
      if (b.loan_word_body_fixed) parts.push(`${b.loan_word_body_fixed} body "loan" word fixed`);
      if (b.thin_body) parts.push(`${b.thin_body} thin/short body`);
      if (b.duplicate_body) parts.push(`${b.duplicate_body} duplicate body deleted`);
      if (b.near_duplicate_body) parts.push(`${b.near_duplicate_body} near-dup body deleted`);
      const detail = parts.length ? ` (${parts.join(", ")})` : "";
      // Subjects are regenerated in place, not deleted — report separately.
      const dupSubj = (b.duplicate_subject || 0) + (b.near_duplicate_subject || 0);
      const shortSubj = b.short_subject || 0;
      const loanSubj = b.loan_word_subject || 0;
      const regen = b.subject_regenerated || 0;
      const queued = b.subject_regen_queued || 0;
      const regenFail = b.subject_regen_failed || 0;
      let subjMsg = "";
      const needRepair = dupSubj + shortSubj + loanSubj;
      const kinds = [];
      if (shortSubj) kinds.push(`${shortSubj} short`);
      if (loanSubj) kinds.push(`${loanSubj} "loan" word`);
      if (dupSubj) kinds.push(`${dupSubj} duplicate`);
      if (queued) {
        subjMsg = `; repairing ${queued} subject${queued === 1 ? "" : "s"} (${kinds.join(" + ")}) in the background, body kept — refresh in a minute`;
      } else if (needRepair) {
        subjMsg = `; ${regen} subject${regen === 1 ? "" : "s"} repaired in place (${kinds.join(" + ")}, body kept)`;
        if (regenFail) subjMsg += `, ${regenFail} left for next sweep`;
      }
      toast.success(`Removed ${r.data.deleted} item${r.data.deleted === 1 ? "" : "s"}${detail}${subjMsg}`);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setBusy(false); }
  };

  const downloadPool = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("ce_token");
      const n = parseInt(dlCount, 10);
      const useCount = Number.isFinite(n) && n > 0;
      const amount = useCount ? Math.min(n, total) : total;
      if (dlDelete && !confirm(`Download ${amount} pool item${amount === 1 ? "" : "s"} AND permanently remove them from the pool? This cannot be undone.`)) {
        setBusy(false);
        return;
      }
      const qs = new URLSearchParams();
      if (useCount) qs.set("count", String(n));
      if (dlDelete) qs.set("consume", "true");
      if (dlWhich && dlWhich !== "all") qs.set("which", dlWhich);
      const res = await fetch(`${API}/content-pool/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const exported = res.headers.get("X-Pool-Exported") || amount;
      const consumed = res.headers.get("X-Pool-Consumed") || "0";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `content_pool_${exported}_items${Number(consumed) > 0 ? "_removed" : ""}_${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (Number(consumed) > 0) {
        toast.success(`Downloaded ${exported} item${exported === "1" ? "" : "s"} and removed ${consumed} from the pool`);
        await load({ hydrate: false });
      } else {
        toast.success(`Downloaded ${exported} pool item${exported === "1" ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const toggleRecycle = async (val) => {
    try {
      const r = await api.put("/content-pool/config", { recycle_2x: val });
      setState((s) => (s ? { ...s, config: { ...s.config, recycle_2x: r.data.config.recycle_2x } } : s));
      toast.success(val
        ? "2× reuse ON — each item is sent to 2 recipients, then deleted"
        : "2× reuse OFF — each item is sent once, then deleted");
    } catch (_e) {
      toast.error("Could not change reuse setting");
    }
  };

  const saveMix = async (pct) => {
    const weight = Math.max(0, Math.min(1, pct / 100));
    try {
      const r = await api.put("/content-pool/config", { recycle_mix_weight: weight });
      const saved = Math.round((r.data.config.recycle_mix_weight ?? weight) * 100);
      setMixPct(saved);
      toast.success(`Mix saved — ${100 - saved}% fresh / ${saved}% reused per send`);
    } catch (_e) {
      toast.error("Could not save mix ratio");
    }
  };

  const deleteByUsage = async (which) => {
    const label = which === "new" ? "NEW (never sent)" : which === "used" ? "USED (sent at least once)" : "ALL";
    if (!confirm(`Permanently delete ${label} content from the pool? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.delete("/content-pool/by-usage", { params: { which } });
      toast.success(`Deleted ${r.data.deleted} ${which} item${r.data.deleted === 1 ? "" : "s"}`);
      await load({ hydrate: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadSubjects = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("ce_token");
      const res = await fetch(`${API}/content-pool/export-subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `content_pool_subjects_${total}_${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${total} subject${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(`Subjects download failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  if (!state || !formHydrated) return <div className="p-8 font-mono text-[#71717A]">[ loading pool... ]</div>;

  const cfg = state.config || {};
  const target = state.effective_target || 0;
  const avail = state.available || 0;
  const pct = target > 0 ? Math.min(100, (avail / target) * 100) : 0;
  const off = state.off_hours_now;
  const peakLabel = state.peak_hours_utc?.map((h) => `${String(h).padStart(2, "0")}:00`).join(", ");
  // Live UTC-clock helper — updates every second so operators can verify
  // whether "now" is actually a peak or off-peak hour.
  const nowClock = <NowClock peakHours={state.peak_hours_utc || []} />;

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="micro mb-1">System</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Content pool</h1>
          <p className="text-xs font-mono text-[#71717A] mt-2 max-w-2xl leading-relaxed">
            Pre-generates AI email content so live campaigns don&apos;t
            wait on DeepSeek. Each item carries verbatim placeholders and binds to one recipient at send-time,
            then is deleted. Auto concurrency: {state.concurrency_auto}; Manual concurrency: {state.concurrency_manual};
            hard cap {state.concurrency_max}.
            <span className="block mt-1 text-[#8E8E93]">
              <b className="text-[#E1FF00]">DeepSeek V4 pricing (mid-Jul 2026 onwards):</b> 2× cost during peak UTC
              hours <span className="text-[#00E5FF]">01:00–04:00</span> &amp; <span className="text-[#00E5FF]">06:00–10:00</span>
              {" "}(Beijing 09:00–12:00 &amp; 14:00–18:00). Enable &quot;Restrict to off-peak&quot; below to auto-pause
              generation during those windows and cut token spend in half.
            </span>
          </p>
        </div>
        <div className={`flex flex-col items-end gap-1`} data-testid="pool-status-header">
          <div className={`flex items-center gap-2 px-3 py-2 border font-mono text-[11px] uppercase tracking-wider ${
            !state.restrict_to_off_hours ? "border-[#E1FF00] text-[#E1FF00]"
            : off ? "border-[#00FF66] text-[#00FF66]"
            : "border-[#FF9500] text-[#FF9500]"}`} data-testid="pool-status-hours">
            {!state.restrict_to_off_hours ? (
              <><Lightning size={14} weight="fill" /> Always on</>
            ) : off ? (
              <><Lightning size={14} weight="fill" /> Off-hours (generating)</>
            ) : (
              <><LightningSlash size={14} weight="fill" /> Peak-hour hold</>
            )}
          </div>
          {nowClock}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 border border-[#2A2A2A] bg-[#0A0A0A] mb-6" data-testid="pool-stats">
        <Stat label="Available" v={avail} tone="brand" sub={`${state.fresh ?? 0} fresh · ${state.used_once ?? 0} used once`} />
        <Stat label="Effective target" v={target} sub={`${cfg.target_count || 0} × ${100 + (cfg.overage_percent || 0)}%`} />
        <Stat label="Manual queue" v={cfg.manual_queue_count || 0} tone={cfg.manual_queue_count ? "warning" : "muted"} />
        <Stat label="Last run" v={cfg.last_generated_count ?? 0} sub={cfg.last_run_status || "idle"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 border border-[#2A2A2A] bg-[#0A0A0A] mb-6" data-testid="pool-reuse-stats">
        <Stat label="Fresh (never sent)" v={state.fresh ?? 0} sub="use_count = 0" />
        <Stat label="Used once (in pool)" v={state.used_once ?? 0} tone="warning" sub="waiting for 2nd use" live />
        <Stat label="Swapped subjects" v={state.swapped_subjects ?? 0} tone="cyan" sub="borrowed alt subject ready" live />
        <Stat label="Recycled & deleted (today)" v={cfg.recycled_deleted_total ?? 0} tone="brand" sub="sent 2× then removed · resets 12 AM PT" />
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] mb-6" data-testid="pool-recycle-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <div className="micro text-[#E1FF00]">Use content twice (2× recycle)</div>
            <div className="text-[11px] text-[#A1A1AA] font-mono mt-0.5">
              {cfg.recycle_2x
                ? "ON — each item is sent to 2 recipients, then deleted (halves how much you need to generate)."
                : "OFF — each item is sent once, then deleted (fresh per send, drains the pool 2× faster)."}
            </div>
          </div>
          <button
            onClick={() => toggleRecycle(!cfg.recycle_2x)}
            data-testid="pool-recycle-toggle"
            aria-pressed={!!cfg.recycle_2x}
            className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${cfg.recycle_2x ? "bg-[#E1FF00]" : "bg-[#2A2A2A]"}`}
            title="Toggle 2× content reuse"
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-black transition-transform ${cfg.recycle_2x ? "translate-x-7" : ""}`} />
          </button>
        </div>

        {/* Mix ratio slider — only meaningful while 2× recycle is ON */}
        <div className={`border-t border-[#2A2A2A] px-4 py-3 ${cfg.recycle_2x ? "" : "opacity-40 pointer-events-none"}`} data-testid="pool-mix-ratio">
          <div className="flex items-center justify-between mb-2">
            <div className="micro text-[#00E5FF]">New / reused mix per send</div>
            <div className="font-mono text-[11px]">
              <span className="text-[#E1FF00]" data-testid="pool-mix-fresh-pct">{100 - mixPct}% fresh</span>
              <span className="text-[#71717A] mx-1">/</span>
              <span className="text-[#FF9500]" data-testid="pool-mix-reused-pct">{mixPct}% reused</span>
            </div>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={mixPct}
            disabled={!cfg.recycle_2x}
            onChange={(e) => setMixPct(Number(e.target.value))}
            onMouseUp={(e) => saveMix(Number(e.target.value))}
            onTouchEnd={(e) => saveMix(Number(e.target.value))}
            onKeyUp={(e) => saveMix(Number(e.target.value))}
            data-testid="pool-mix-slider"
            className="w-full accent-[#00E5FF] cursor-pointer"
          />
          <div className="text-[10px] font-mono text-[#71717A] mt-1 leading-relaxed">
            Chance each send draws a <b className="text-[#FF9500]">used-once</b> item (its 2nd &amp; final use,
            with the cross-swapped subject) instead of a <b className="text-[#E1FF00]">fresh</b> one.
            Higher = reuse spreads out sooner &amp; the pool drains slower. Applies instantly, no redeploy.
          </div>
        </div>
      </div>

      {cfg.last_run_status === "ai_key_missing" && (
        <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-4 mb-6 flex items-center gap-3" data-testid="pool-ai-key-missing">
          <Warning size={16} weight="fill" className="text-[#FF3B30]" />
          <div className="text-xs font-mono text-[#FF3B30] leading-relaxed">
            <b>DeepSeek API key not configured.</b> Pool generation is paused. Add your key
            in <a href="/settings" className="underline text-[#00E5FF]">Settings → DeepSeek</a> to resume.
          </div>
        </div>
      )}

      {cfg.last_error && (
        <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-4 mb-6" data-testid="pool-last-error">
          <div className="flex items-start gap-3">
            <Warning size={16} weight="fill" className="text-[#FF3B30] shrink-0 mt-0.5" />
            <div className="text-xs font-mono text-[#FF3B30] leading-relaxed break-all">
              <b>Last generation error{cfg.last_error_source ? ` (${cfg.last_error_source})` : ""}:</b><br />
              {cfg.last_error}
              {cfg.last_error_at && (
                <span className="block mt-1 text-[10px] text-[#71717A]">
                  at {new Date(cfg.last_error_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-4 mb-6" data-testid="pool-progress">
        <div className="flex justify-between items-center mb-2">
          <div className="micro">Fill level</div>
          <div className="font-mono text-xs text-[#A1A1AA]">
            {avail} / {target || "—"} {target > 0 ? `(${pct.toFixed(1)}%)` : ""}
          </div>
        </div>
        <div className="h-2 w-full bg-[#141414] overflow-hidden">
          <div
            className="h-full bg-[#E1FF00] transition-all duration-300"
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        <div className="mt-2 text-[10px] font-mono text-[#71717A]">
          Peak UTC hours (used only when &quot;Restrict to off-peak&quot; is on): {peakLabel}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Auto config */}
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-5" data-testid="pool-config-card">
          <div className="flex items-center gap-2 mb-3">
            <Database size={14} weight="bold" className="text-[#E1FF00]" />
            <div className="micro text-[#E1FF00]">Auto pool config</div>
          </div>
          <p className="text-[11px] text-[#71717A] font-mono mb-4">
            Auto-fill runs during off-hours until <code>target + overage</code> items are ready.
            If you don&apos;t update this daily, the worker keeps refilling to the same target every day.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <label className="block">
              <span className="micro block mb-1">Target count</span>
              <input
                type="number"
                value={form.target_count}
                min="0"
                onChange={(e) => setForm({ ...form, target_count: e.target.value })}
                data-testid="pool-target-input"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
                placeholder="e.g. 6000"
              />
            </label>
            <label className="block">
              <span className="micro block mb-1">Overage %</span>
              <input
                type="number"
                value={form.overage_percent}
                min="0"
                max="200"
                onChange={(e) => setForm({ ...form, overage_percent: e.target.value })}
                data-testid="pool-overage-input"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="micro block mb-1">
                Auto concurrency
                <span className="text-[#71717A] normal-case ml-1">(1–{state.concurrency_max})</span>
              </span>
              <input
                type="number"
                value={form.pool_concurrency_auto}
                min="1"
                max={state.concurrency_max}
                onChange={(e) => setForm({ ...form, pool_concurrency_auto: e.target.value })}
                data-testid="pool-concurrency-auto-input"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
              />
            </label>
          </div>
          <label className="block mb-3">
            <span className="micro block mb-1">Auto model</span>
            <select
              value={form.pool_auto_model || "deepseek_direct_flash"}
              onChange={(e) => setForm({ ...form, pool_auto_model: e.target.value })}
              data-testid="pool-auto-model-select"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
            >
              <ModelOptions models={aiModels} includeRouting />
            </select>
          </label>
          <label className="block mb-3">
            <span className="micro block mb-1">Auto thinking mode</span>
            <select
              value={form.pool_auto_thinking || ""}
              onChange={(e) => setForm({ ...form, pool_auto_thinking: e.target.value })}
              data-testid="pool-auto-thinking-select"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
            >
              <option value="">Default (model decides)</option>
              <option value="disabled">Disabled (fast · ~2-5s)</option>
              <option value="enabled">Enabled (reasoning · ~15-30s)</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-mono mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_enabled}
              onChange={(e) => setForm({ ...form, auto_enabled: e.target.checked })}
              data-testid="pool-auto-toggle"
              className="accent-[#E1FF00]"
            />
            <span>Auto pool generation enabled</span>
          </label>
          <label className="flex items-start gap-2 text-xs font-mono mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={form.restrict_to_off_hours}
              onChange={(e) => setForm({ ...form, restrict_to_off_hours: e.target.checked })}
              data-testid="pool-off-hours-toggle"
              className="accent-[#E1FF00] mt-0.5"
            />
            <span className="leading-relaxed">
              Restrict auto fills to <b>off-peak UTC hours</b> only
              <span className="ml-2 text-[10px] px-1.5 py-0.5 border border-[#00FF66] text-[#00FF66] uppercase tracking-wider">
                saves ~50% during peak
              </span>
              <span className="block text-[10px] text-[#71717A]">
                Peak UTC skipped: {peakLabel} — DeepSeek V4 doubles token pricing at these times
                (Beijing 09:00–12:00 &amp; 14:00–18:00). Manual fills always run regardless.
              </span>
            </span>
          </label>
          <BtnPrimary onClick={saveConfig} disabled={saving} testid="pool-save-config">
            {saving ? "Saving..." : "Save config"}
          </BtnPrimary>
        </div>

        {/* Manual fill */}
        <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-5" data-testid="pool-manual-card">
          <div className="flex items-center gap-2 mb-3">
            <Plus size={14} weight="bold" className="text-[#00E5FF]" />
            <div className="micro text-[#00E5FF]">Manual fill</div>
          </div>
          <p className="text-[11px] text-[#71717A] font-mono mb-4">
            Queue a specific number of generations right now. Runs at manual concurrency
            <span className="text-[#00E5FF] mx-1">{state.concurrency_manual}</span>
            and ignores the off-peak restriction (it&apos;s on-demand). Bounded so DeepSeek isn&apos;t overloaded.
          </p>
          <div className="mb-3">
            <label className="block">
              <span className="micro block mb-1">
                Manual concurrency
                <span className="text-[#71717A] normal-case ml-1">(1–{state.concurrency_max})</span>
              </span>
              <input
                type="number"
                value={form.pool_concurrency_manual}
                min="1"
                max={state.concurrency_max}
                onChange={(e) => setForm({ ...form, pool_concurrency_manual: e.target.value })}
                onBlur={saveConfig}
                data-testid="pool-concurrency-manual-input"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#00E5FF] focus:outline-none"
              />
              <span className="text-[10px] text-[#71717A] font-mono mt-1 block">Saves on blur.</span>
            </label>
          </div>
          <div className="mb-3">
            <label className="block">
              <span className="micro block mb-1">Manual model</span>
              <select
                value={form.pool_manual_model || "deepseek_direct_flash"}
                onChange={(e) => setForm({ ...form, pool_manual_model: e.target.value })}
                onBlur={saveConfig}
                data-testid="pool-manual-model-select"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#00E5FF] focus:outline-none"
              >
                <ModelOptions models={aiModels} includeRouting />
              </select>
            </label>
          </div>
          <div className="mb-3">
            <label className="block">
              <span className="micro block mb-1">Manual thinking mode</span>
              <select
                value={form.pool_manual_thinking || ""}
                onChange={(e) => setForm({ ...form, pool_manual_thinking: e.target.value })}
                onBlur={saveConfig}
                data-testid="pool-manual-thinking-select"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#00E5FF] focus:outline-none"
              >
                <option value="">Default (model decides)</option>
                <option value="disabled">Disabled (fast · ~2-5s)</option>
                <option value="enabled">Enabled (reasoning · ~15-30s)</option>
              </select>
            </label>
          </div>
          <div className="flex gap-2 mb-3">
            <input
              type="number"
              value={manualCount}
              min="1"
              onChange={(e) => setManualCount(e.target.value)}
              data-testid="pool-manual-count"
              className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white font-mono focus:border-[#00E5FF] focus:outline-none"
              placeholder="how many?"
            />
            <button
              onClick={manualFill}
              disabled={busy || !manualCount}
              data-testid="pool-manual-fill-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-mono uppercase tracking-wider bg-[#00E5FF] text-black hover:bg-[#00C4DA] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={13} weight="bold" /> Queue fill
            </button>
          </div>
          {(cfg.manual_queue_count || 0) > 0 && (
            <div className="flex items-center justify-between border border-[#FF9500]/40 bg-[#FF9500]/10 px-3 py-2">
              <span className="font-mono text-[11px] text-[#FF9500]">
                Pending queue: {cfg.manual_queue_count}
              </span>
              <button
                onClick={cancelQueue}
                data-testid="pool-cancel-queue"
                className="text-[10px] uppercase font-mono tracking-wider text-[#FF9500] hover:text-white"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pool items table */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="micro text-[#E1FF00]">Ready items ({total})</h2>
        <div className="flex gap-2">
          <button
            onClick={downloadSubjects}
            disabled={busy || total === 0}
            data-testid="pool-download-subjects-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider border border-[#E1FF00] text-[#E1FF00] hover:bg-[#E1FF00]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={`Download ${total} subject${total === 1 ? "" : "s"} only (one per line)`}
          >
            <DownloadSimple size={13} weight="bold" /> Download subjects
          </button>
          <div className="flex items-center gap-1.5 border border-[#00E5FF]/40 pl-2">
            <select
              value={dlWhich}
              onChange={(e) => setDlWhich(e.target.value)}
              data-testid="pool-download-which"
              title="Which items to download: all, only new (never sent) or only used (sent at least once)"
              className="bg-transparent text-xs font-mono text-[#00E5FF] py-2 focus:outline-none cursor-pointer"
            >
              <option value="all" className="bg-black">all</option>
              <option value="new" className="bg-black">new only</option>
              <option value="used" className="bg-black">used only</option>
            </select>
            <input
              type="number" min="1" max={total || undefined}
              value={dlCount}
              onChange={(e) => setDlCount(e.target.value)}
              placeholder={`all (${total})`}
              data-testid="pool-download-count-input"
              className="w-24 bg-transparent text-xs font-mono text-white py-2 focus:outline-none placeholder:text-[#52525B]"
              title="How many oldest items to download. Leave empty for all."
            />
            <label className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] cursor-pointer select-none px-1" title="Also remove the downloaded items from the pool">
              <input type="checkbox" checked={dlDelete}
                onChange={(e) => setDlDelete(e.target.checked)}
                data-testid="pool-download-delete-toggle" className="accent-[#00E5FF]" />
              delete
            </label>
            <button
              onClick={downloadPool}
              disabled={busy || total === 0}
              data-testid="pool-download-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider border-l border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title={dlDelete ? "Download the chosen items and remove them from the pool" : "Download the chosen items (kept in pool)"}
            >
              <DownloadSimple size={13} weight="bold" /> {dlDelete ? "Download & remove" : "Download .txt"}
            </button>
          </div>
          <BtnDestructive onClick={clearPool} disabled={busy || total === 0} testid="pool-clear-btn">
            <Trash size={13} weight="bold" /> Clear pool
          </BtnDestructive>
          <BtnSecondary onClick={() => deleteByUsage("new")} disabled={busy || total === 0} testid="pool-delete-new-btn">
            <Trash size={13} weight="bold" /> Delete new
          </BtnSecondary>
          <BtnSecondary onClick={() => deleteByUsage("used")} disabled={busy} testid="pool-delete-used-btn">
            <Trash size={13} weight="bold" /> Delete used
          </BtnSecondary>
          <BtnSecondary onClick={clearBrokenItems} disabled={busy || total === 0} testid="pool-clear-broken-btn">
            <Trash size={13} weight="bold" /> Purge broken only
          </BtnSecondary>
          <BtnSecondary onClick={clearHardcodedNameItems} disabled={busy || total === 0} testid="pool-clear-hardcoded-btn">
            <Trash size={13} weight="bold" /> Purge hardcoded names
          </BtnSecondary>
          <BtnSecondary onClick={clearRepetitivePhraseItems} disabled={busy || total === 0} testid="pool-clear-repetitive-btn">
            <Trash size={13} weight="bold" /> Purge repetitive & duplicates
          </BtnSecondary>
        </div>
      </div>

      {cleanupStats && (
        <div className="mb-3 border border-[#1f3a2a] bg-[#0b1710] px-4 py-2.5 flex items-center gap-5 flex-wrap" data-testid="cleanup-stats-banner">
          <span className="font-mono text-[11px] text-[#5fd08a] font-bold uppercase tracking-wide">Cleanup / repair sweep</span>

          {/* Live repair backlog — the single signal for "is turbo done?" */}
          {(cleanupStats.unclean_remaining || 0) > 0 ? (
            <span className="font-mono text-[12px] text-[#cfe9d8]" data-testid="cleanup-unclean-remaining">
              Unclean items remaining: <b className="text-[#F4C430]">{(cleanupStats.unclean_remaining || 0).toLocaleString()}</b>
              <span className="text-[#7f9c8a]"> / {(cleanupStats.total_ready || 0).toLocaleString()} ready</span>
            </span>
          ) : (
            <span className="font-mono text-[12px] text-[#5fd08a] font-bold" data-testid="cleanup-complete">
              ✓ Repair complete — safe to resume sending
            </span>
          )}

          {/* Turbo / Gentle switch */}
          <span className="font-mono text-[11px] flex items-center gap-1.5" data-testid="sweep-mode-switch">
            <span className="text-[#7f9c8a] uppercase tracking-wide">Mode:</span>
            <button
              type="button"
              onClick={() => setSweepMode("turbo")}
              className={`px-2 py-0.5 border transition-colors ${cleanupStats.sweep_mode === "turbo" ? "bg-[#F4C430] text-black border-[#F4C430] font-bold" : "text-[#7f9c8a] border-[#2A2A2A] hover:border-[#F4C430]"}`}
              data-testid="sweep-mode-turbo-btn"
              title="Fast backlog drain — use while sending is paused"
            >
              Turbo
            </button>
            <button
              type="button"
              onClick={() => setSweepMode("gentle")}
              className={`px-2 py-0.5 border transition-colors ${cleanupStats.sweep_mode === "gentle" ? "bg-[#5fd08a] text-black border-[#5fd08a] font-bold" : "text-[#7f9c8a] border-[#2A2A2A] hover:border-[#5fd08a]"}`}
              data-testid="sweep-mode-gentle-btn"
              title="Low CPU — safe to leave on during live campaigns"
            >
              Gentle
            </button>
          </span>

          <span className="font-mono text-[12px] text-[#cfe9d8]" data-testid="cleanup-fixed">
            Fixed total: <b className="text-white">{(cleanupStats.total_fixed || 0).toLocaleString()}</b>
          </span>
          <span className="font-mono text-[12px] text-[#cfe9d8]" data-testid="cleanup-remaining">
            Remaining subjects: <b className={cleanupStats.remaining_subjects > 0 ? "text-[#F4C430]" : "text-white"}>{(cleanupStats.remaining_subjects || 0).toLocaleString()}</b>
          </span>
          <span className="font-mono text-[11px] text-[#7f9c8a]" data-testid="cleanup-breakdown">
            (subjects {(cleanupStats.total_subjects_regenerated || 0).toLocaleString()} · bodies {(cleanupStats.total_bodies_fixed || 0).toLocaleString()} · links {(((cleanupStats.total_cta_repaired || 0) + (cleanupStats.total_cta_anchor_fixed || 0))).toLocaleString()} · normalized {(cleanupStats.total_normalized_subjects || 0).toLocaleString()} · deleted {(cleanupStats.total_deleted || 0).toLocaleString()})
          </span>
          {cleanupStats.last_fixed_this_pass > 0 && (
            <span className="font-mono text-[11px] text-[#5fd08a]">+{cleanupStats.last_fixed_this_pass.toLocaleString()} last sweep</span>
          )}
        </div>
      )}


      {throttled.length > 0 && (
        <div className="mb-3 border border-[#F4C430] bg-[#1a1500] px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="throttle-banner">
          <span className="font-mono text-[11px] text-[#F4C430] font-bold uppercase tracking-wide">⚠ Auto-throttled / blocked:</span>
          {throttled.map((t) => (
            <span key={t.model_key} title={t.reason || ""} className={`font-mono text-[10px] border px-2 py-0.5 ${t.blocked ? "text-[#FF5F56] border-[#FF5F56]" : "text-[#F4C430] border-[#F4C430]"}`} data-testid={`throttle-${t.model_key}`}>
              {t.model_display} · {t.blocked ? "BLOCKED by provider (403 — model off on your FindClix account) · " : ""}{Math.round(t.cooldown_remaining_s)}s left{t.strikes > 1 ? ` · ${t.strikes}× ` : ""}
            </span>
          ))}
        </div>
      )}

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="pool-items-table">
          <thead>
            <tr>
              <th>Generated</th>
              <th>Intent</th>
              <th>Pattern</th>
              <th>Subject</th>
              <th>Model</th>
              <th>Body preview</th>
              <th>Source</th>
              <th className="num">Duration</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center text-[#71717A] py-8 font-mono">[ pool is empty ]</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id}>
                <td className="font-mono text-[10px] text-[#71717A]">{it.generated_at?.slice(11, 19)}</td>
                <td className="font-mono text-[11px] text-[#00E5FF]">{it.spec_intent}</td>
                <td className="font-mono text-[11px]">{it.spec_subject_pattern}</td>
                <td className="text-xs max-w-xs">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{it.subject}</span>
                    <SubjLen subject={it.subject} min={cfg?.ai_settings?.subject_min_chars ?? 40} max={cfg?.ai_settings?.subject_max_chars ?? 60} />
                  </div>
                </td>
                <td className="font-mono text-[10px] text-[#F4C430] whitespace-nowrap" data-testid={`pool-item-model-${it.id}`}>{it.gen_model || "—"}</td>
                <td className="font-mono text-[10px] text-[#A1A1AA] truncate max-w-md">{it.text_body?.slice(0, 80)}…</td>
                <td>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${it.source === "manual" ? "border-[#00E5FF] text-[#00E5FF]" : "border-[#71717A] text-[#A1A1AA]"}`}>
                    {it.source}
                  </span>
                </td>
                <td className="num data-num">{it.duration_ms}ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
          sizeOptions={[100, 200, 250, 500]}
          testidPrefix="pool-pg"
        />
      )}
    </div>
  );
}

function Stat({ label, v, sub, tone, live }) {
  const toneClass = tone === "brand" ? "text-[#E1FF00]"
    : tone === "warning" ? "text-[#FF9500]"
    : tone === "cyan" ? "text-[#00E5FF]"
    : tone === "muted" ? "text-[#71717A]"
    : "text-white";
  return (
    <div className="p-4 border-r border-b border-[#2A2A2A]">
      <div className="micro mb-2 flex items-center gap-1.5">
        {label}
        {live && (
          <span className="relative flex h-1.5 w-1.5" title="Updates live every 5s">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF66] opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#00FF66]" />
          </span>
        )}
      </div>
      <div className={`data-num text-3xl font-bold ${toneClass}`}>{(v ?? 0).toLocaleString?.() ?? v ?? "—"}</div>
      {sub && <div className="text-[10px] font-mono text-[#71717A] mt-1">{sub}</div>}
    </div>
  );
}

/**
 * Live UTC + local-time clock so operators can verify at a glance
 * whether the current hour is one of the configured peak-UTC hours.
 * Also computes the next peak window boundary so users can see
 * exactly when the worker WILL pause / resume.
 */
function NowClock({ peakHours }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  const utcH = now.getUTCHours();
  const utcM = now.getUTCMinutes();
  const utcS = now.getUTCSeconds();
  const pad = (n) => String(n).padStart(2, "0");
  const utcStr = `${pad(utcH)}:${pad(utcM)}:${pad(utcS)}`;
  const localStr = now.toLocaleTimeString();
  const isPeak = (peakHours || []).includes(utcH);
  // Find next boundary — either the next peak start (if off) or the
  // next off-peak start (if in peak). Both wrap around 24h.
  const sortedPeak = [...(peakHours || [])].sort((a, b) => a - b);
  let nextLabel = "";
  if (sortedPeak.length) {
    if (isPeak) {
      // walk forward until next hour NOT in the set
      let h = utcH;
      while (sortedPeak.includes(h)) h = (h + 1) % 24;
      nextLabel = `off-peak resumes at ${pad(h)}:00 UTC`;
    } else {
      // find next peak hour
      const nextP = sortedPeak.find((h) => h > utcH);
      const h = nextP != null ? nextP : sortedPeak[0];
      nextLabel = `next peak at ${pad(h)}:00 UTC`;
    }
  }
  return (
    <div className="text-[10px] font-mono text-[#71717A] leading-tight text-right" data-testid="pool-now-clock">
      <div>
        <span className="text-[#00E5FF]">UTC</span> {utcStr}
        {isPeak && <span className="ml-2 text-[#FF9500]">peak</span>}
        {!isPeak && <span className="ml-2 text-[#00FF66]">off-peak</span>}
      </div>
      <div>local {localStr}</div>
      <div className="text-[#71717A]">{nextLabel}</div>
    </div>
  );
}

function SubjLen({ subject, min, max }) {
  const n = (subject || "").replace(/\[[^\]]+\]/g, " ").replace(/\s+/g, " ").trim().length;
  const bad = n < min || n > max;
  return (
    <span
      data-testid="pool-item-subject-len"
      title={`${n} real chars (placeholders NOT counted) — rule ${min}-${max}`}
      className={`shrink-0 text-[10px] font-mono px-1 border ${bad ? "text-[#FF3B3B] border-[#FF3B3B]/50" : "text-[#71717A] border-[#27272A]"}`}
    >
      {n}
    </span>
  );
}
